import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
var breakpointMap = {
  xs: 0,
  sm: 400,
  md: 640,
  lg: 900,
  xl: 1200,
  xxl: 1640
};
var palette = {
  white: '#ffffff',
  black: '#000000',
  moonpink: {
    base: '#ffa2c1',
    d40: '#f25f90',
    d60: '#ee447d',
    d80: '#d63d70'
  },
  eclipse: {
    base: '#505050',
    d20: '#404040',
    l20: '#737373',
    l40: '#969696',
    l60: '#b9b9b9',
    l80: '#dcdcdc',
    l90: '#ededed',
    l95: '#f6f6f6',
    l98: '#fafafa'
  },
  aurora: {
    base: '#57e99a',
    d60: '#00a88c'
  },
  horizon: {
    base: '#61d1ff',
    d60: '#179fd9'
  },
  dusk: {
    base: '#ff778f',
    d60: '#ca2a53',
    l90: '#fff1f4'
  },
  dawn: '#ffd334',
  crescent: '#feea71',
  solar: '#fe912a',
  lunar: '#dde2e8',
  calypso: '#4fc5c2',
  green: '#00a88c',
  red: '#ff0f00'
};
var colors = {
  palette: palette,
  primary: palette.moonpink.base,
  cta: palette.moonpink.d60,
  ctaActive: palette.moonpink.d40,
  onCta: palette.white,
  site: palette.eclipse.l98,
  text: palette.eclipse.base,
  success: palette.green,
  error: palette.red,
  placeholder: palette.eclipse.l60
};
var BASE_FONT_SIZE = 16;
var FONT_FAMILY_MOONPIG = 'Moonpig, sans-serif';
var FONT_WEIGHT_BOLD = 700;
var FONT_WEIGHT_LIGHT = 300;

var pxToRem = function pxToRem(size) {
  return "".concat(size / BASE_FONT_SIZE, "rem");
};

var TYPES = {
  type100: [14, 24],
  type200: [16, 24],
  type300: [18, 24],
  type400: [20, 28],
  type500: [24, 32],
  type600: [32, 40],
  type700: [40, 48],
  type800: [48, 56],
  type900: [56, 64],
  type1000: [64, 72]
};

var createType = function createType(type) {
  var _TYPES$type = _slicedToArray(TYPES[type], 2),
      fontSizePx = _TYPES$type[0],
      lineHeightPx = _TYPES$type[1];

  return {
    fontSize: pxToRem(fontSizePx),
    lineHeight: lineHeightPx / fontSizePx
  };
};

var typography = {
  //types
  type100: createType('type100'),
  type200: createType('type200'),
  type300: createType('type300'),
  type400: createType('type400'),
  type500: createType('type500'),
  type600: createType('type600'),
  type700: createType('type700'),
  type800: createType('type800'),
  type900: createType('type900'),
  type1000: createType('type1000'),
  // //variants
  bodyLabel: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontFamily: FONT_FAMILY_MOONPIG,
    map: 'type100'
  },
  links: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontFamily: FONT_FAMILY_MOONPIG,
    map: 'type200'
  },
  ctaButton: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontFamily: FONT_FAMILY_MOONPIG,
    letterSpacing: '0.4px',
    map: 'type200'
  },
  body: {
    fontWeight: FONT_WEIGHT_LIGHT,
    fontFamily: FONT_FAMILY_MOONPIG,
    map: 'type200'
  },
  display1: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontFamily: FONT_FAMILY_MOONPIG,
    map: 'type200'
  },
  display2: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontFamily: FONT_FAMILY_MOONPIG,
    map: {
      xs: 'type300',
      md: 'type400'
    }
  },
  display3: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontFamily: FONT_FAMILY_MOONPIG,
    map: {
      xs: 'type400',
      md: 'type500'
    }
  },
  display4: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontFamily: FONT_FAMILY_MOONPIG,
    map: {
      xs: 'type500',
      md: 'type600'
    }
  },
  display5: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontFamily: FONT_FAMILY_MOONPIG,
    map: 'type600'
  },
  display6: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontFamily: FONT_FAMILY_MOONPIG,
    map: 'type700'
  },
  display7: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontFamily: FONT_FAMILY_MOONPIG,
    map: 'type800'
  },
  display8: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontFamily: FONT_FAMILY_MOONPIG,
    map: 'type900'
  },
  display9: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontFamily: FONT_FAMILY_MOONPIG,
    map: 'type1000'
  }
};
/* If you update this please update `spacingType` too */

var spacingValues = [0, 1, 2, 4, 8, 12, 16, 24, 32, 48, 64, 96, 128, 192, 256]; // https://github.com/babel/babel/issues/8361

var theme = {
  baseFontSize: BASE_FONT_SIZE,
  colors: colors,
  typography: typography,
  spacing: spacingValues,
  breakpoints: {
    map: breakpointMap,
    keys: Object.keys(breakpointMap),
    up: function up(key) {
      return "@media (min-width: ".concat(breakpointMap[key] / BASE_FONT_SIZE, "em)");
    },
    down: function down(key) {
      return "@media (max-width: ".concat(breakpointMap[key] / BASE_FONT_SIZE - 0.01, "em)");
    }
  },
  siteWidth: 1200,
  // Nb. @material-ui/system currently looks for 'palette' on theme
  // This may change in https://github.com/mui-org/material-ui/issues/13663
  // at which point we could drop this alias
  palette: colors
};

var colorValue = function colorValue(color) {
  return function (_ref) {
    var theme = _ref.theme;
    return theme.colors[color];
  };
};

var spacing = function spacing(value) {
  return function (_ref2) {
    var theme = _ref2.theme;
    return theme.spacing[value];
  };
};

var spacingPx = function spacingPx(value) {
  return function (_ref3) {
    var theme = _ref3.theme;
    return "".concat(spacing(value)({
      theme: theme
    }), "px");
  };
}; // https://github.com/babel/babel/issues/8361


var theme$1 = theme; // Export interfaces

export { colorValue, spacing, spacingPx, theme$1 as theme };